// /app/calendar — Week view across all brands, restyled from the handoff.
//
// Per design/Flows.html Flow 06 + 2026-05-22 handoff:
//   Three views: Week grid · Brand swimlanes · Status kanban.
//   Drag-to-reschedule preserved (HTML5 DnD, desktop only).
//
// Backend reads (per-brand fan-out, since a workspace-wide /posts endpoint
// doesn't exist yet):
//   GET /v1/brands/:brandId/posts?from=&to=
// All brand pages are merged client-side. Optimistic reschedule uses
// PATCH /v1/posts/:id { scheduled_for }.

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type CursorPage } from "@/lib/api";
import { useBrands } from "@/hooks/use-persona";
import { usePostDrawer } from "@/components/PostDrawer";
import { ManualComposer } from "@/components/ManualComposer";
import { Icons, PlatformIcon } from "@/components/icons";
import { BrandMark, PlatformChip, PageHead, Badge } from "@/components/primitives";
import { useIsMobile } from "@/lib/use-is-mobile";
import type { Brand, Platform, Post } from "@/lib/types";

export const Route = createFileRoute("/app/calendar")({
  component: CalendarPage,
});

type ViewMode = "week" | "swim" | "kanban";

const PLATFORM_META: Record<Platform, { name: string; color: string }> = {
  instagram: { name: "Instagram", color: "#E1306C" },
  facebook: { name: "Facebook", color: "#1877F2" },
  x: { name: "X", color: "#FFFFFF" },
  linkedin: { name: "LinkedIn", color: "#0A66C2" },
  telegram: { name: "Telegram", color: "#26A5E4" },
};

const STATUS_META: Record<string, { label: string; labelEN: string }> = {
  draft: { label: "Taslak", labelEN: "Draft" },
  scheduled: { label: "Planlandı", labelEN: "Scheduled" },
  publishing: { label: "Yayınlanıyor", labelEN: "Publishing" },
  published: { label: "Yayınlandı", labelEN: "Published" },
  failed: { label: "Başarısız", labelEN: "Failed" },
  cancelled: { label: "İptal", labelEN: "Cancelled" },
};

function CalendarPage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [view, setView] = useState<ViewMode>("week");
  const [anchor, setAnchor] = useState<Date>(() => startOfWeek(new Date()));
  const days = useMemo(() => daysOfWeek(anchor), [anchor]);
  const weekStart = days[0];
  const weekEnd = endOfDay(days[6]);

  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [platformFilter, setPlatformFilter] = useState<Platform | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const [trayOpen, setTrayOpen] = useState(true);

  const { data: brandsData } = useBrands();
  const brands = brandsData?.data ?? [];

  // Single workspace-wide query via content-service v0.2.73's
  // GET /v1/posts?brand=&from=&to= — replaces the per-brand
  // useQueries fan-out (which was N HTTP calls per page).
  const postsParams = new URLSearchParams({
    from: weekStart.toISOString(),
    to: weekEnd.toISOString(),
    limit: "500",
  });
  if (brandFilter) postsParams.set("brand", brandFilter);
  const { data: postsRes, isLoading } = useQuery({
    queryKey: ["calendar", brandFilter ?? "all", weekStart.toISOString()],
    queryFn: () => api<CursorPage<Post>>(`/posts?${postsParams.toString()}`),
    staleTime: 15_000,
  });
  const allPosts: Post[] = useMemo(() => postsRes?.data ?? [], [postsRes]);

  // Unscheduled drafts — approving a generation without picking a slot
  // leaves scheduled_for NULL, and the week grid can only place posts
  // that have a timestamp, so those rows were invisible on every week.
  // Fetched with NO from/to so they surface no matter which week is on
  // screen, then rendered in a tray above the grid.
  const unschedParams = new URLSearchParams({ status: "draft", limit: "500" });
  if (brandFilter) unschedParams.set("brand", brandFilter);
  const { data: unschedRes } = useQuery({
    queryKey: ["unscheduled", brandFilter ?? "all"],
    queryFn: () => api<CursorPage<Post>>(`/posts?${unschedParams.toString()}`),
    staleTime: 15_000,
  });
  const unscheduled: Post[] = useMemo(
    () =>
      (unschedRes?.data ?? [])
        .filter((p) => !p.scheduled_for && !p.published_at)
        .filter((p) => !platformFilter || p.platform === platformFilter)
        .slice()
        .sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? "")),
    [unschedRes, platformFilter],
  );

  const filtered = useMemo(
    () =>
      allPosts.filter(
        (p) =>
          (!platformFilter || p.platform === platformFilter) &&
          (!statusFilter || p.status === statusFilter),
      ),
    [allPosts, platformFilter, statusFilter],
  );

  const qc = useQueryClient();
  // POST /posts/:id/schedule, not PATCH: the backend's patchPostReq only
  // accepts text_body / hashtags / ai_disclosure / comments_disabled and
  // 400s with "no fields to update" on a scheduled_for-only body, so the
  // drag was silently doing nothing. /schedule is the canonical path and
  // also promotes an undated draft to 'scheduled' in the same call.
  const reschedule = useMutation({
    mutationFn: ({ id, scheduled_for }: { id: string; scheduled_for: string }) =>
      api<Post>(`/posts/${id}/schedule`, { method: "POST", body: { scheduled_for } }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["unscheduled"] });
      qc.invalidateQueries({ queryKey: ["posts"] });
    },
  });

  const onReschedule = (postId: string, day: Date) => {
    const post =
      filtered.find((p) => p.id === postId) ??
      unscheduled.find((p) => p.id === postId);
    if (!post) return;
    const next = new Date(day);
    if (post.scheduled_for || post.published_at) {
      // Keep the time-of-day when moving an already-dated post.
      const cur = new Date(post.scheduled_for || post.published_at || day);
      next.setHours(cur.getHours(), cur.getMinutes(), 0, 0);
    } else {
      // Undated draft dropped on a day — 09:00 local is a sane default.
      next.setHours(9, 0, 0, 0);
    }
    if (next.toISOString() === post.scheduled_for) return;
    // The backend rejects anything under a minute out — say why instead
    // of firing a request that can only 400.
    if (next.getTime() < Date.now() + 60_000) {
      setDropError(
        tr
          ? "Geçmiş bir güne planlayamazsın — ileri bir gün seç."
          : "Can't schedule into the past — pick a future day.",
      );
      return;
    }
    setDropError(null);
    reschedule.mutate({ id: postId, scheduled_for: next.toISOString() });
  };

  const dateLabel = `${days[0].toLocaleDateString(tr ? "tr-TR" : "en-US", {
    day: "2-digit",
    month: "short",
  })} – ${days[6].toLocaleDateString(tr ? "tr-TR" : "en-US", {
    day: "2-digit",
    month: "short",
  })}`;

  const scheduledCount = filtered.filter((p) => p.status === "scheduled").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={tr ? `04 · Takvim · ${dateLabel}` : `04 · Calendar · ${dateLabel}`}
        title={tr ? "Bu hafta neler yayınlanıyor." : "What ships this week."}
        subtitle={
          tr
            ? `${filtered.length} / ${allPosts.length} gönderi gösteriliyor · sürükleyip planı değiştirebilirsin.`
            : `${filtered.length} of ${allPosts.length} posts shown · drag to reschedule.`
        }
        actions={
          <>
            <button
              className="btn btn-secondary btn-sm"
              aria-label={tr ? "Önceki hafta" : "Previous week"}
              onClick={() => setAnchor(addDays(anchor, -7))}
            >
              <Icons.ChevronLeft size={13} />
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setAnchor(startOfWeek(new Date()))}
            >
              {tr ? "Bugün" : "Today"}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              aria-label={tr ? "Sonraki hafta" : "Next week"}
              onClick={() => setAnchor(addDays(anchor, 7))}
            >
              <Icons.ChevronRight size={13} />
            </button>
            <div style={{ width: 8 }} />
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setComposerOpen(true)}
            >
              <Icons.Plus size={13} /> {tr ? "Gönderi oluştur" : "Create post"}
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate({ to: "/app/generate" })}
            >
              <Icons.Sparkles size={13} /> {tr ? "Üret" : "Generate"}
            </button>
          </>
        }
      />

      <ManualComposer
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        brands={brands}
        defaultBrandId={brandFilter}
      />

      {/* Toolbar */}
      <div
        style={{
          borderBottom: "1px solid var(--b1)",
          padding: "12px 32px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            background: "var(--surface)",
            border: "1px solid var(--b2)",
            borderRadius: 8,
            padding: 3,
          }}
        >
          {(
            [
              { id: "week", label: tr ? "Hafta" : "Week", I: Icons.Calendar },
              { id: "swim", label: tr ? "Şeritler" : "Swimlanes", I: Icons.Layers },
              { id: "kanban", label: tr ? "Durum" : "Status", I: Icons.Filter },
            ] as const
          ).map((v) => {
            const I = v.I;
            const on = view === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id as ViewMode)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 5,
                  fontSize: 12,
                  background: on ? "var(--elev)" : "transparent",
                  color: on ? "var(--tx)" : "var(--tx2)",
                  border: on ? "1px solid var(--b3)" : "1px solid transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: on ? 600 : 500,
                }}
              >
                <I size={12} />
                {v.label}
              </button>
            );
          })}
        </div>

        <FilterChip
          label={tr ? "Marka" : "Brand"}
          value={brandFilter}
          onClear={() => setBrandFilter(null)}
          options={brands.map((b) => ({
            id: b.id,
            label: b.trade_name,
            mark: b,
          }))}
          onSelect={(id) => setBrandFilter(id)}
        />
        <FilterChip
          label={tr ? "Platform" : "Platform"}
          value={platformFilter}
          onClear={() => setPlatformFilter(null)}
          options={Object.entries(PLATFORM_META).map(([id, m]) => ({
            id,
            label: m.name,
            color: m.color,
          }))}
          onSelect={(id) => setPlatformFilter(id as Platform)}
        />
        <FilterChip
          label={tr ? "Durum" : "Status"}
          value={statusFilter}
          onClear={() => setStatusFilter(null)}
          options={Object.entries(STATUS_META).map(([id, m]) => ({
            id,
            label: tr ? m.label : m.labelEN,
          }))}
          onSelect={(id) => setStatusFilter(id)}
        />

        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: "var(--mu)" }}>
          {tr
            ? `${filtered.length} gönderi · ${scheduledCount} planlandı`
            : `${filtered.length} posts · ${scheduledCount} scheduled`}
        </span>
      </div>

      <div className="scroll-y" style={{ flex: 1, padding: isMobile ? "0 0 24px" : 24, background: "var(--bg)" }}>
        {dropError || reschedule.error ? (
          <div
            role="alert"
            style={{
              margin: isMobile ? "12px 16px" : "0 0 12px",
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 12,
              color: "var(--error)",
              background: "rgba(239,68,68,0.08)",
              border: "1px solid rgba(239,68,68,0.3)",
            }}
          >
            {dropError ??
              (tr ? "Planlama kaydedilemedi." : "Couldn't save the new time.")}
          </div>
        ) : null}

        <UnscheduledTray
          posts={unscheduled}
          brands={brands}
          tr={tr}
          open={trayOpen}
          onToggle={() => setTrayOpen((o) => !o)}
          isMobile={isMobile}
        />

        {isLoading ? (
          <CalendarSkeleton />
        ) : isMobile ? (
          // Mobile: handoff's day-chip strip + vertical post list per
          // selected day. The desktop swim / kanban views aren't useful
          // on a phone so we always use the per-day list there.
          <MobileWeekView days={days} posts={filtered} brands={brands} tr={tr} />
        ) : view === "week" ? (
          <WeekView days={days} posts={filtered} brands={brands} onReschedule={onReschedule} />
        ) : view === "swim" ? (
          <SwimlaneView days={days} posts={filtered} brands={brands} tr={tr} />
        ) : (
          <KanbanView posts={filtered} brands={brands} tr={tr} />
        )}
      </div>
    </div>
  );
}

// ── Unscheduled tray ──────────────────────────────────────────────────────
//
// Drafts with scheduled_for = NULL can't be placed in the week grid, so
// before this they were invisible on every week — the operator's
// "I added posts and they disappeared". The tray sits above the grid,
// is week-independent, and each card is draggable onto a day column
// (same text/post-id payload the grid pills use) or clickable to open
// PostDrawer, which has the date/time picker.
function UnscheduledTray({
  posts,
  brands,
  tr,
  open,
  onToggle,
  isMobile,
}: {
  posts: Post[];
  brands: Brand[];
  tr: boolean;
  open: boolean;
  onToggle: () => void;
  isMobile: boolean;
}) {
  const openDrawer = usePostDrawer((s) => s.open);
  const brandMap = useMemo(() => new Map(brands.map((b) => [b.id, b])), [brands]);
  if (posts.length === 0) return null;

  return (
    <div
      className="card"
      style={{
        margin: isMobile ? "12px 16px" : "0 0 16px",
        padding: 0,
        border: "1px dashed rgba(245,158,11,0.5)",
        background: "rgba(245,158,11,0.05)",
        overflow: "hidden",
      }}
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          background: "transparent",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <Icons.Sparkles size={13} style={{ color: "#F59E0B", flexShrink: 0 }} />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--tx)" }}>
          {tr ? "Zamanlanmamış taslaklar" : "Unscheduled drafts"}
        </span>
        <span
          className="mono"
          style={{
            fontSize: 10,
            padding: "1px 7px",
            borderRadius: 999,
            background: "rgba(245,158,11,0.18)",
            color: "#F59E0B",
          }}
        >
          {posts.length}
        </span>
        <span style={{ fontSize: 11, color: "var(--mu)" }}>
          {tr
            ? "· üretildi ama saati seçilmedi — bir güne sürükle ya da aç"
            : "· generated but never given a time — drag onto a day, or open"}
        </span>
        <div style={{ flex: 1 }} />
        <Icons.ChevronRight
          size={13}
          style={{
            color: "var(--mu)",
            transform: open ? "rotate(90deg)" : "none",
            transition: "transform 150ms",
          }}
        />
      </button>

      {open ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: "0 14px 12px",
            overflowX: "auto",
          }}
        >
          {posts.map((p) => {
            const brand = brandMap.get(p.brand_id);
            const accent = brand?.accent_hex || "var(--accent)";
            return (
              <div
                key={p.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/post-id", p.id);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onClick={() => openDrawer(p.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") openDrawer(p.id);
                }}
                title={
                  tr
                    ? "Bir güne sürükle ya da tıklayıp saatini seç"
                    : "Drag onto a day, or click to pick a time"
                }
                style={{
                  flex: "0 0 210px",
                  padding: 10,
                  background: "var(--surface)",
                  border: "1px solid var(--b2)",
                  borderLeft: `3px solid ${accent}`,
                  borderRadius: 8,
                  cursor: "grab",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    lineHeight: 1.35,
                    color: "var(--tx)",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    wordBreak: "break-word",
                  }}
                >
                  {truncateCaption(p.text_body, 60) || (tr ? "(metin yok)" : "(no text)")}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <PlatformIcon platform={p.platform} size={11} />
                  <span
                    style={{
                      fontSize: 10,
                      color: "var(--mu)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {brand?.trade_name ?? ""}
                  </span>
                  <div style={{ flex: 1 }} />
                  <Badge status="pending" label={tr ? "Zamanlanmadı" : "Unscheduled"} />
                </div>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    openDrawer(p.id);
                  }}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {tr ? "Zaman seç" : "Schedule"}
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

// ── Filter chip dropdown ──────────────────────────────────────────────────

function FilterChip<T extends string>({
  label,
  value,
  onClear,
  options,
  onSelect,
}: {
  label: string;
  value: T | null;
  onClear: () => void;
  options: { id: string; label: string; color?: string; mark?: Brand }[];
  onSelect: (id: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.id === value);
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 6,
          background: value ? "var(--accent-soft)" : "var(--surface)",
          border: `1px dashed ${value ? "var(--accent)" : "var(--b2)"}`,
          fontSize: 12,
          color: value ? "var(--accent)" : "var(--tx2)",
        }}
      >
        <span style={{ color: "var(--mu)" }}>{label}</span>
        {selected ? (
          <b style={{ color: "var(--accent)" }}>{selected.label}</b>
        ) : (
          <span>+</span>
        )}
        {value ? (
          <span
            role="button"
            aria-label="clear"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            style={{ marginLeft: 2, display: "inline-flex" }}
          >
            <Icons.X size={11} />
          </span>
        ) : null}
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            background: "var(--surface)",
            border: "1px solid var(--b3)",
            borderRadius: 8,
            padding: 4,
            minWidth: 200,
            zIndex: 50,
            boxShadow: "0 14px 40px rgba(0,0,0,0.5)",
          }}
        >
          {options.map((o) => (
            <button
              key={o.id}
              onMouseDown={() => {
                onSelect(o.id);
                setOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "6px 10px",
                borderRadius: 4,
                fontSize: 12,
                color: "var(--tx)",
                textAlign: "left",
                background: "transparent",
              }}
            >
              {o.mark ? (
                <BrandMark brand={o.mark} size={14} />
              ) : o.color ? (
                <span
                  style={{ width: 8, height: 8, borderRadius: 2, background: o.color }}
                />
              ) : null}
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ── Week view: 7-day grid w/ drag-drop ────────────────────────────────────

function WeekView({
  days,
  posts,
  brands,
  onReschedule,
}: {
  days: Date[];
  posts: Post[];
  brands: Brand[];
  onReschedule: (postId: string, day: Date) => void;
}) {
  const brandMap = new Map(brands.map((b) => [b.id, b]));
  const postsByDay = useMemo(() => {
    const map = new Map<string, Post[]>();
    for (const p of posts) {
      const t = p.scheduled_for || p.published_at;
      if (!t) continue;
      const key = dayKey(new Date(t));
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    for (const v of map.values()) {
      v.sort((a, b) => (a.scheduled_for ?? "").localeCompare(b.scheduled_for ?? ""));
    }
    return map;
  }, [posts]);

  return (
    <div
      className="card cal-week-shell"
      style={{
        padding: 0,
        overflow: "hidden",
      }}
    >
      <div
        className="cal-week-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
        }}
      >
        {days.map((d) => (
          <DayColumn
            key={d.toISOString()}
            day={d}
            posts={postsByDay.get(dayKey(d)) ?? []}
            brandMap={brandMap}
            onReschedule={(postId) => onReschedule(postId, d)}
          />
        ))}
      </div>
    </div>
  );
}

function DayColumn({
  day,
  posts,
  brandMap,
  onReschedule,
}: {
  day: Date;
  posts: Post[];
  brandMap: Map<string, Brand>;
  onReschedule: (postId: string) => void;
}) {
  const [over, setOver] = useState(false);
  const today = isSameDay(day, new Date());
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!over) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const id = e.dataTransfer.getData("text/post-id");
        if (id) onReschedule(id);
      }}
      style={{
        borderLeft: "1px solid var(--b1)",
        background: over
          ? "var(--accent-soft)"
          : today
            ? "rgba(255,255,255,0.02)"
            : "transparent",
        minHeight: 400,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          padding: "12px 10px",
          borderBottom: "1px solid var(--b1)",
          background: today ? "var(--accent-soft)" : "transparent",
        }}
      >
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: today ? "var(--accent)" : "var(--mu)",
            letterSpacing: "0.15em",
            textTransform: "uppercase",
          }}
        >
          {day.toLocaleDateString("tr-TR", { weekday: "short" })}
        </div>
        <div
          className="t-num"
          style={{ fontSize: 16, color: today ? "var(--accent)" : "var(--tx)" }}
        >
          {day.getDate()}
        </div>
      </div>
      <div style={{ flex: 1, padding: 6, display: "flex", flexDirection: "column", gap: 4 }}>
        {posts.length === 0 ? (
          <p
            style={{
              color: "var(--mu)",
              fontSize: 11,
              textAlign: "center",
              padding: "16px 0",
              fontFamily: "var(--mono)",
            }}
          >
            —
          </p>
        ) : (
          posts.map((p) => <CalPost key={p.id} post={p} brand={brandMap.get(p.brand_id)} />)
        )}
      </div>
    </div>
  );
}

// ── Swimlane view: brand rows × days ──────────────────────────────────────

function SwimlaneView({
  days,
  posts,
  brands,
  tr,
}: {
  days: Date[];
  posts: Post[];
  brands: Brand[];
  tr: boolean;
}) {
  const byBrand = useMemo(() => {
    const map = new Map<string, Post[]>();
    for (const p of posts) {
      const arr = map.get(p.brand_id) ?? [];
      arr.push(p);
      map.set(p.brand_id, arr);
    }
    return map;
  }, [posts]);

  return (
    <div className="card" style={{ padding: 0, overflow: "auto" }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "180px 1fr",
          borderBottom: "1px solid var(--b1)",
          position: "sticky",
          top: 0,
          background: "var(--surface)",
          zIndex: 2,
        }}
      >
        <div style={{ padding: "14px 16px" }}>
          <div
            className="mono"
            style={{
              fontSize: 10,
              color: "var(--mu)",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
            }}
          >
            {tr ? "Marka" : "Brand"}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
          {days.map((d) => {
            const today = isSameDay(d, new Date());
            return (
              <div
                key={d.toISOString()}
                style={{
                  padding: "10px 12px",
                  borderLeft: "1px solid var(--b1)",
                  background: today
                    ? "linear-gradient(180deg, var(--accent-soft), transparent)"
                    : "transparent",
                }}
              >
                <div
                  className="mono"
                  style={{
                    fontSize: 10,
                    color: today ? "var(--accent)" : "var(--mu)",
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                  }}
                >
                  {d.toLocaleDateString("tr-TR", { weekday: "short" })}
                </div>
                <div
                  className="t-num"
                  style={{ fontSize: 14, color: today ? "var(--accent)" : "var(--tx)" }}
                >
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {brands.map((b) => {
        const brandPosts = byBrand.get(b.id) ?? [];
        if (brandPosts.length === 0) return null;
        const accent = b.accent_hex || "var(--accent)";
        return (
          <div
            key={b.id}
            style={{
              display: "grid",
              gridTemplateColumns: "180px 1fr",
              borderBottom: "1px solid var(--b1)",
              minHeight: 80,
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                background: accent,
              }}
            />
            <div
              style={{
                padding: "14px 16px",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <BrandMark brand={b} size={26} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{b.trade_name}</div>
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                  {tr ? `${brandPosts.length} gönderi` : `${brandPosts.length} posts`}
                </div>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                position: "relative",
              }}
            >
              {days.map((d) => (
                <div
                  key={d.toISOString()}
                  style={{
                    borderLeft: "1px solid var(--b1)",
                    padding: 8,
                    minHeight: 80,
                  }}
                >
                  {brandPosts
                    .filter((p) => {
                      const t = p.scheduled_for || p.published_at;
                      return t && isSameDay(new Date(t), d);
                    })
                    .map((p) => (
                      <CalPost key={p.id} post={p} brand={b} swim />
                    ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Kanban view: columns by status ────────────────────────────────────────

function KanbanView({ posts, brands, tr }: { posts: Post[]; brands: Brand[]; tr: boolean }) {
  const cols: { id: string; label: string }[] = [
    { id: "scheduled", label: tr ? "Planlandı" : "Scheduled" },
    { id: "publishing", label: tr ? "Yayınlanıyor" : "Publishing" },
    { id: "published", label: tr ? "Yayınlandı" : "Published" },
    { id: "failed", label: tr ? "İlgi gerekiyor" : "Needs attention" },
    { id: "draft", label: tr ? "Taslaklar" : "Drafts" },
  ];
  const brandMap = new Map(brands.map((b) => [b.id, b]));

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, 1fr)",
        gap: 12,
        minWidth: 1100,
      }}
    >
      {cols.map((col) => {
        const items = posts.filter((p) => p.status === col.id);
        return (
          <div key={col.id} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 4px 8px",
              }}
            >
              <Badge status={col.id} label={col.label} />
              <span className="mono" style={{ fontSize: 11, color: "var(--mu)" }}>
                {items.length}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                minHeight: 200,
                padding: 8,
                background: "var(--surface)",
                border: "1px dashed var(--b2)",
                borderRadius: 8,
              }}
            >
              {items.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 24,
                    color: "var(--mu)",
                    fontSize: 11,
                    fontFamily: "var(--mono)",
                  }}
                >
                  — {tr ? "boş" : "empty"} —
                </div>
              ) : (
                items.map((p) => (
                  <CalPost key={p.id} post={p} brand={brandMap.get(p.brand_id)} kanban />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Post pill (compact / swim / kanban variants) ──────────────────────────

// MobileWeekView — handoff's day-chip strip + vertical post list.
// Renders below the page header on phones in place of the desktop
// 7-col grid (which only ever showed 3 days at 390px viewport).
function MobileWeekView({
  days,
  posts,
  brands,
  tr,
}: {
  days: Date[];
  posts: Post[];
  brands: Brand[];
  tr: boolean;
}) {
  // Anchor the initial day to today when it falls inside this week,
  // otherwise to Monday. Lets "Today" feel like a fresh state.
  const todayIdx = days.findIndex((d) => isSameDay(d, new Date()));
  const [activeIdx, setActiveIdx] = useState<number>(todayIdx >= 0 ? todayIdx : 0);
  const brandMap = useMemo(() => {
    const m = new Map<string, Brand>();
    brands.forEach((b) => m.set(b.id, b));
    return m;
  }, [brands]);
  const postsByDay = useMemo(() => {
    const m = new Map<string, Post[]>();
    posts.forEach((p) => {
      const w = p.scheduled_for || p.published_at;
      if (!w) return;
      const k = dayKey(new Date(w));
      const arr = m.get(k) ?? [];
      arr.push(p);
      m.set(k, arr);
    });
    return m;
  }, [posts]);
  const sorted = (postsByDay.get(dayKey(days[activeIdx])) ?? [])
    .slice()
    .sort((a, b) => {
      const ta = a.scheduled_for || a.published_at || "";
      const tb = b.scheduled_for || b.published_at || "";
      return ta.localeCompare(tb);
    });

  const open = usePostDrawer((s) => s.open);

  const dayNamesShort = tr
    ? ["PZT", "SAL", "ÇAR", "PER", "CUM", "CMT", "PAZ"]
    : ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Day-chip strip — horizontally scrollable so all 7 days reachable. */}
      <div
        style={{
          display: "flex",
          gap: 6,
          padding: "12px 16px 16px",
          overflowX: "auto",
          scrollbarWidth: "none",
          flexShrink: 0,
        }}
      >
        {days.map((d, i) => {
          const active = i === activeIdx;
          const isTodayCell = isSameDay(d, new Date());
          const dayPosts = postsByDay.get(dayKey(d)) ?? [];
          return (
            <button
              key={d.toISOString()}
              onClick={() => setActiveIdx(i)}
              style={{
                flex: "0 0 56px",
                padding: "10px 6px",
                background: active ? "var(--accent)" : "var(--surface)",
                border: `1px solid ${active ? "var(--accent)" : isTodayCell ? "var(--accent-soft)" : "var(--b2)"}`,
                borderRadius: 10,
                color: active ? "#fff" : "var(--tx)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 4,
                transition: "background 200ms, color 200ms",
                cursor: "pointer",
              }}
            >
              <span
                className="mono"
                style={{
                  fontSize: 9,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: active ? "#fff" : "var(--mu)",
                }}
              >
                {dayNamesShort[i]}
              </span>
              <span style={{ fontFamily: "var(--display)", fontSize: 16, fontWeight: 700 }}>
                {d.getDate()}
              </span>
              <span
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: dayPosts.length > 0
                    ? (active ? "#fff" : "var(--accent)")
                    : "transparent",
                }}
              />
            </button>
          );
        })}
      </div>

      {/* Day post list — vertical, one card per post. */}
      <div style={{ padding: "0 16px" }}>
        <div className="eyebrow" style={{ marginBottom: 10 }}>
          — {sorted.length} {tr ? "gönderi" : "post"}
          {sorted.length === 1 ? "" : tr ? "" : "s"}
          {" · "}
          {days[activeIdx].toLocaleDateString(tr ? "tr-TR" : "en-US", {
            weekday: "long",
            day: "numeric",
            month: "short",
          })}
        </div>
        {sorted.length === 0 ? (
          <div
            style={{
              padding: "32px 16px",
              textAlign: "center",
              color: "var(--mu)",
              fontSize: 13,
              border: "1px dashed var(--b2)",
              borderRadius: 8,
            }}
          >
            {tr ? "Bu gün için planlanan gönderi yok." : "No posts scheduled for this day."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {sorted.map((p) => {
              const brand = brandMap.get(p.brand_id);
              const when = p.scheduled_for || p.published_at;
              const time = when
                ? new Date(when).toLocaleTimeString(tr ? "tr-TR" : "en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—";
              const accent = brand?.accent_hex || "var(--accent)";
              return (
                <button
                  key={p.id}
                  onClick={() => open(p.id)}
                  style={{
                    padding: 12,
                    background: "var(--surface)",
                    border: "1px solid var(--b2)",
                    borderLeft: `3px solid ${accent}`,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    textAlign: "left",
                    color: "var(--tx)",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 7,
                      background: brand?.accent_soft_hex || "var(--accent-soft)",
                      color: accent,
                      border: `1px solid ${accent}55`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "var(--display)",
                      fontWeight: 700,
                      fontSize: 11,
                      flexShrink: 0,
                    }}
                  >
                    {(brand?.trade_name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 12.5,
                        fontWeight: 500,
                        marginBottom: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {truncateCaption(p.text_body, 50) || (tr ? "(metin yok)" : "(no text)")}
                    </div>
                    <div
                      className="mono"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 9,
                        color: "var(--mu)",
                      }}
                    >
                      <PlatformIcon platform={p.platform} size={10} />
                      <span>
                        {brand?.trade_name ?? ""} · {time}
                      </span>
                    </div>
                  </div>
                  <Badge status={p.status} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CalPost({
  post,
  brand,
  swim,
  kanban,
}: {
  post: Post;
  brand: Brand | undefined;
  swim?: boolean;
  kanban?: boolean;
}) {
  const open = usePostDrawer((s) => s.open);
  const when = post.scheduled_for || post.published_at;
  const time = when
    ? new Date(when).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
    : "—";
  const statusColor =
    post.status === "published"
      ? "var(--success)"
      : post.status === "scheduled"
        ? "var(--info)"
        : post.status === "publishing"
          ? "var(--warning)"
          : post.status === "failed"
            ? "var(--error)"
            : "var(--mu)";

  const accent = brand?.accent_hex || "var(--accent)";
  const accentSoft = brand?.accent_soft_hex || "var(--accent-soft)";
  // Truncate to ~40 chars (~6-8 words) so post tiles don't blow out
  // their column. Full caption is in PostDrawer when the user taps in.
  const title = truncateCaption(post.text_body, 40) || "(metin yok)";

  const draggable = post.status === "scheduled" || post.status === "draft";

  if (kanban) {
    return (
      <button
        onClick={() => open(post.id)}
        draggable={draggable}
        onDragStart={(e) => {
          e.dataTransfer.setData("text/post-id", post.id);
          e.dataTransfer.effectAllowed = "move";
        }}
        style={{
          padding: 10,
          background: "var(--elev)",
          border: "1px solid var(--b2)",
          borderLeft: `3px solid ${accent}`,
          borderRadius: 6,
          textAlign: "left",
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.3,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            wordBreak: "break-word",
          }}
        >
          {title}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {brand ? <BrandMark brand={brand} size={14} /> : null}
          <span style={{ fontSize: 10, color: "var(--mu)" }}>{brand?.trade_name}</span>
          <div style={{ flex: 1 }} />
          <PlatformChip platform={post.platform} size={16} />
        </div>
        <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
          {time}
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={() => open(post.id)}
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/post-id", post.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 7px",
        width: "100%",
        background: `linear-gradient(90deg, ${accentSoft}, transparent)`,
        borderLeft: `2px solid ${accent}`,
        borderRadius: 4,
        textAlign: "left",
        marginBottom: 3,
        cursor: draggable ? "grab" : "pointer",
      }}
      title={`${post.platform} · ${time}`}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: statusColor,
          flexShrink: 0,
        }}
      />
      <PlatformIcon platform={post.platform} size={10} />
      <span
        style={{
          flex: 1,
          fontSize: 11,
          color: "var(--tx)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {title}
      </span>
      {!swim ? (
        <span className="mono" style={{ fontSize: 9, color: "var(--mu)" }}>
          {time}
        </span>
      ) : null}
    </button>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────

function CalendarSkeleton() {
  return (
    <div className="card" style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="skel"
            style={{ height: 400, borderLeft: "1px solid var(--b1)" }}
          />
        ))}
      </div>
    </div>
  );
}

// ── Date helpers ──────────────────────────────────────────────────────────

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  const dow = x.getDay();
  const mondayOffset = (dow + 6) % 7;
  x.setDate(x.getDate() - mondayOffset);
  x.setHours(0, 0, 0, 0);
  return x;
}

function daysOfWeek(monday: Date): Date[] {
  return Array.from({ length: 7 }).map((_, i) => addDays(monday, i));
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

// truncateCaption — break on a word boundary, append "…" when cut.
// Replaces blanket .slice(0, N) which chopped mid-word + mid-emoji
// (the emoji squares operator saw in the mobile calendar).
function truncateCaption(s: string | undefined | null, max = 40): string {
  if (!s) return "";
  const flat = s.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  // back up to the last space within the budget so we don't slice
  // mid-word; if none, hard-cut at the budget.
  let cut = flat.slice(0, max);
  const sp = cut.lastIndexOf(" ");
  if (sp > max * 0.6) cut = cut.slice(0, sp);
  return cut + "…";
}

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

function isSameDay(a: Date, b: Date): boolean {
  return dayKey(a) === dayKey(b);
}
